<template>
  <div class="gestione-depositi">
    <div class="page-header">
      <h1>Gestione Depositi</h1>
      <p class="subtitle">
        Sezione dedicata alla consultazione e gestione dei depositi cauzionali dei clienti.
      </p>
    </div>

    <div class="page-content">
      <div v-if="loading" class="loader-card">
        <span>Caricamento depositi cauzionali...</span>
      </div>

      <div v-else-if="error" class="error-card">
        <h2>Errore</h2>
        <p>{{ error }}</p>
      </div>

      <div v-else class="table-wrapper">
        <div v-if="insertError" class="inline-error">
          {{ insertError }}
        </div>
        <div v-if="depositi.length === 0" class="empty-state">
          <h2>Nessun deposito trovato</h2>
          <p>Non risultano depositi cauzionali che soddisfano i criteri di ricerca.</p>
        </div>

        <div v-else>
          <table class="depositi-table">
            <thead>
              <tr>
                <th
                  :class="getSortClass('numero')"
                  @click="handleSort('numero')"
                >
                  <span class="th-label">
                    Numero Fattura
                    <span
                      v-if="sortKey === 'numero'"
                      class="sort-indicator"
                    >
                      {{ sortDirection === 'asc' ? '▲' : '▼' }}
                    </span>
                  </span>
                </th>
                <th
                  :class="getSortClass('mese_fatturazione')"
                  @click="handleSort('mese_fatturazione')"
                >
                  <span class="th-label">
                    Mese Fatturazione
                    <span
                      v-if="sortKey === 'mese_fatturazione'"
                      class="sort-indicator"
                    >
                      {{ sortDirection === 'asc' ? '▲' : '▼' }}
                    </span>
                  </span>
                </th>
                <th
                  :class="getSortClass('data_emissione')"
                  @click="handleSort('data_emissione')"
                >
                  <span class="th-label">
                    Data Emissione
                    <span
                      v-if="sortKey === 'data_emissione'"
                      class="sort-indicator"
                    >
                      {{ sortDirection === 'asc' ? '▲' : '▼' }}
                    </span>
                  </span>
                </th>
                <th
                  :class="getSortClass('dep_cauz')"
                  @click="handleSort('dep_cauz')"
                >
                  <span class="th-label">
                    Deposito
                    <span
                      v-if="sortKey === 'dep_cauz'"
                      class="sort-indicator"
                    >
                      {{ sortDirection === 'asc' ? '▲' : '▼' }}
                    </span>
                  </span>
                </th>
                <th
                  :class="getSortClass('importo_deposito')"
                  @click="handleSort('importo_deposito')"
                >
                  <span class="th-label">
                    Importo deposito
                    <span
                      v-if="sortKey === 'importo_deposito'"
                      class="sort-indicator"
                    >
                      {{ sortDirection === 'asc' ? '▲' : '▼' }}
                    </span>
                  </span>
                </th>
                <th>
                  Deposito Voce
                </th>
                <th
                  :class="getSortClass('code')"
                  @click="handleSort('code')"
                >
                  <span class="th-label">
                    Code
                    <span
                      v-if="sortKey === 'code'"
                      class="sort-indicator"
                    >
                      {{ sortDirection === 'asc' ? '▲' : '▼' }}
                    </span>
                  </span>
                </th>
                <th
                  :class="getSortClass('mese')"
                  @click="handleSort('mese')"
                >
                  <span class="th-label">
                    Mese
                    <span
                      v-if="sortKey === 'mese'"
                      class="sort-indicator"
                    >
                      {{ sortDirection === 'asc' ? '▲' : '▼' }}
                    </span>
                  </span>
                </th>
                <th>
                  Cliente
                </th>
                <th
                  :class="getSortClass('saldo_totale')"
                  @click="handleSort('saldo_totale')"
                >
                  <span class="th-label">
                    Saldo totale
                    <span
                      v-if="sortKey === 'saldo_totale'"
                      class="sort-indicator"
                    >
                      {{ sortDirection === 'asc' ? '▲' : '▼' }}
                    </span>
                  </span>
                </th>
                <th>
                  Rappresentante
                </th>
                <th>
                  Agente
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in sortedDepositi"
                :key="row.numero + '-' + (row.cod_cliente || '')"
                :class="{ 'row-deposito-ok': !!row.deposito_sufficiente }"
              >
                <td>
                  <router-link
                    v-if="row.numero"
                    :to="{ name: 'AskFattureClientiDettagli', params: { numero: row.numero } }"
                    class="cell-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ row.numero }}
                  </router-link>
                  <span v-else>-</span>
                </td>
                <td>{{ row.mese_fatturazione }}</td>
                <td>{{ formatDate(row.data_emissione) }}</td>
                <td class="text-right">
                  {{ formatCurrency(row.dep_cauz) }}
                </td>
                <td class="text-right">
                  {{ formatCurrency(row.importo_deposito) }}
                </td>
                <td class="text-center">
                  <span v-if="!!row.ha_voce_36" class="check-ok">✓</span>
                  <button
                    v-else
                    type="button"
                    class="btn btn-small"
                    :disabled="insertLoading"
                    @click="openInsertDialog(row)"
                  >
                    Inserisci
                  </button>
                </td>
                <td>
                  <template v-if="row.cod_pod">
                    <router-link
                      :to="{ name: 'AskPodsDettagli', params: { id: row.cod_pod } }"
                      class="cell-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {{ row.pod_code || row.cod_pod }}
                    </router-link>
                  </template>
                  <template v-else-if="row.cod_pdr">
                    <router-link
                      :to="{ name: 'AskPdrsDettagli', params: { id: row.cod_pdr } }"
                      class="cell-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {{ row.pdr_code || row.cod_pdr }}
                    </router-link>
                  </template>
                  <span v-else>{{ row.pod_code || row.pdr_code || '-' }}</span>
                </td>
                <td>{{ formatMese(row.pod_data_fine || row.pdr_data_fine) }}</td>
                <td>
                  <router-link
                    v-if="row.cod_cliente"
                    :to="{ name: 'AskClientiDettagli', params: { id: row.cod_cliente } }"
                    class="cell-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ row.cliente }}
                  </router-link>
                  <span v-else>{{ row.cliente || '-' }}</span>
                </td>
                <td class="text-right">
                  {{
                    formatCurrency(
                      row.cod_pod
                        ? row.saldo_totale_pod
                        : (row.cod_pdr ? row.saldo_totale_pdr : null)
                    )
                  }}
                </td>
                <td>
                  <router-link
                    v-if="row.cod_rappresentante"
                    :to="{ name: 'AskRappresentantiDettagli', params: { id: row.cod_rappresentante } }"
                    class="cell-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ row.rappresentante || '-' }}
                  </router-link>
                  <span v-else>{{ row.rappresentante || '-' }}</span>
                </td>
                <td>
                  <router-link
                    v-if="row.cod_agente"
                    :to="{ name: 'AskAgentiDettagli', params: { id: row.cod_agente } }"
                    class="cell-link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ row.agente || '-' }}
                  </router-link>
                  <span v-else>{{ row.agente || '-' }}</span>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="pagination-bar">
            <div class="pagination-actions">
              <button
                type="button"
                class="btn"
                :disabled="pagination.current_page <= 1 || loading"
                @click="goToPage(1)"
              >
                Prima
              </button>
              <button
                type="button"
                class="btn"
                :disabled="pagination.current_page <= 1 || loading"
                @click="goToPage(pagination.current_page - 1)"
              >
                Precedente
              </button>
              <button
                type="button"
                class="btn"
                :disabled="pagination.current_page >= pagination.last_page || loading"
                @click="goToPage(pagination.current_page + 1)"
              >
                Successiva
              </button>
              <button
                type="button"
                class="btn"
                :disabled="pagination.current_page >= pagination.last_page || loading"
                @click="goToPage(pagination.last_page)"
              >
                Ultima
              </button>

              <div class="page-jump">
                <span class="page-jump-label">Vai a</span>
                <input
                  v-model="pageInput"
                  type="number"
                  class="page-input"
                  :min="1"
                  :max="pagination.last_page"
                  inputmode="numeric"
                  :disabled="loading"
                  @keydown.enter.prevent="submitPageInput"
                />
                <button
                  type="button"
                  class="btn"
                  :disabled="loading"
                  @click="submitPageInput"
                >
                  Vai
                </button>
              </div>
            </div>
          </div>
          <div class="pagination-info">
              <span>Pagina {{ pagination.current_page }} di {{ pagination.last_page }}</span>
              <span> | Totale: {{ pagination.total }} record</span>
            </div>
        </div>
      </div>
    </div>
  </div>

  <div
    v-if="showInsertDialog"
    class="modal-backdrop"
    @click.self="closeInsertDialog"
  >
    <div class="modal">
      <h3 class="modal-title">Vuoi inserire la voce in fattura?</h3>
      <p class="modal-subtitle">Riepilogo inserimento voce 36 (Deposito cauzionale)</p>

      <div v-if="selectedRow" class="modal-summary">
        <div class="summary-row">
          <span class="summary-label">Fattura</span>
          <span class="summary-value">{{ selectedRow.numero }}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">Tipo</span>
          <span class="summary-value">{{ selectedRow.cod_pod ? 'POD' : (selectedRow.cod_pdr ? 'PDR' : '-') }}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">Code</span>
          <span class="summary-value">{{ selectedRow.pod_code || selectedRow.pdr_code || '-' }}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">Mese</span>
          <span class="summary-value">{{ formatMese(selectedRow.pod_data_fine || selectedRow.pdr_data_fine) }}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">Importo</span>
          <span class="summary-value">€ {{ formatCurrency(-Math.abs(Number(selectedRow.dep_cauz) || 0)) }}</span>
        </div>
        <div class="summary-row">
          <span class="summary-label">Descrizione</span>
          <span class="summary-value">{{ selectedRow.cod_pod ? 'Deposito cauzionale' : 'DEPOSITO CAUZIONALE GAS' }}</span>
        </div>
      </div>

      <div v-if="insertDialogError" class="modal-error">
        {{ insertDialogError }}
      </div>

      <div class="modal-actions">
        <button
          type="button"
          class="btn"
          :disabled="insertLoading"
          @click="closeInsertDialog"
        >
          Annulla
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="insertLoading"
          @click="confirmInsert"
        >
          {{ insertLoading ? 'Inserimento...' : 'Conferma' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, getCurrentInstance, onMounted, watch } from 'vue';
import Service from '@/services/ask-fatturazione/fatturazione-service';

export default {
  name: 'GestioneDepositi',
  setup() {
    const loading = ref(false);
    const error = ref(null);
    const depositi = ref([]);
    const pagination = ref({
      current_page: 1,
      per_page: 50,
      total: 0,
      last_page: 1,
      from: 0,
      to: 0
    });

    const sortKey = ref(null);
    const sortDirection = ref('asc');
    const pageInput = ref('1');
    const showInsertDialog = ref(false);
    const selectedRow = ref(null);
    const insertLoading = ref(false);
    const insertError = ref(null);
    const insertDialogError = ref(null);

    const self = getCurrentInstance();
    self.$eventBus = self.appContext.config.globalProperties.$eventBus;
    self.$http = self.appContext.config.globalProperties.$http;
    const service = new Service(self.$http, self.$eventBus);

    const loadDepositi = async (page = 1) => {
      loading.value = true;
      error.value = null;

      try {
        const res = await service.getDepositiCauzionali(page, pagination.value.per_page);
        depositi.value = res.items || [];
        pagination.value = res.pagination || pagination.value;
      } catch (e) {
        console.error(e);
        error.value = 'Errore durante il caricamento dei depositi cauzionali.';
      } finally {
        loading.value = false;
      }
    };

    const goToPage = (page) => {
      if (page < 1 || page > pagination.value.last_page) return;
      loadDepositi(page);
    };

    const submitPageInput = () => {
      const raw = pageInput.value;
      const parsed = parseInt(String(raw), 10);
      if (!Number.isFinite(parsed)) return;

      const max = Number(pagination.value.last_page) || 1;
      const target = Math.min(Math.max(parsed, 1), max);
      pageInput.value = String(target);
      goToPage(target);
    };

    const openInsertDialog = (row) => {
      selectedRow.value = row;
      insertDialogError.value = null;
      showInsertDialog.value = true;
    };

    const closeInsertDialog = () => {
      showInsertDialog.value = false;
      selectedRow.value = null;
      insertDialogError.value = null;
    };

    const confirmInsert = async () => {
      if (!selectedRow.value) return;
      insertLoading.value = true;
      insertDialogError.value = null;
      insertError.value = null;

      try {
        const res = await service.insertDepositoCauzionale({
          used_ft: selectedRow.value.numero,
          cod_pod: selectedRow.value.cod_pod,
          cod_pdr: selectedRow.value.cod_pdr
        });

        if (res && res.success === true) {
          selectedRow.value.ha_voce_36 = true;
          selectedRow.value.importo_deposito = -Math.abs(Number(selectedRow.value.dep_cauz) || 0);
          closeInsertDialog();
        } else {
          insertDialogError.value = res?.message || 'Inserimento non riuscito';
        }
      } catch (e) {
        const message =
          e?.response?.data?.message ||
          'Errore durante l’inserimento del deposito cauzionale';
        insertDialogError.value = message;
        insertError.value = message;
      } finally {
        insertLoading.value = false;
      }
    };

    const formatCurrency = (value) => {
      if (value === null || value === undefined) return '-';
      const num = Number(value);
      if (isNaN(num)) return '-';
      return num.toLocaleString('it-IT', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    };

    const formatDate = (value) => {
      if (!value) return '-';
      const date = new Date(value);
      if (isNaN(date.getTime())) return value;
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };

    const formatMese = (value) => {
      if (!value) return '-';
      const date = new Date(value);
      if (isNaN(date.getTime())) return value;
      const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
      const day = String(firstDay.getDate()).padStart(2, '0');
      const month = String(firstDay.getMonth() + 1).padStart(2, '0');
      const year = firstDay.getFullYear();
      return `${day}/${month}/${year}`;
    };

    const sortedDepositi = computed(() => {
      if (!sortKey.value) {
        return depositi.value;
      }

      const key = sortKey.value;
      const direction = sortDirection.value === 'asc' ? 1 : -1;

      const numericKeys = ['dep_cauz', 'importo_deposito', 'saldo_totale'];
      const dateKeys = ['data_emissione', 'pod_data_fine', 'pdr_data_fine', 'mese'];

      const items = [...depositi.value];

      items.sort((a, b) => {
        let aVal = a[key];
        let bVal = b[key];

        if (key === 'code') {
          aVal = a.pod_code || a.pdr_code || '';
          bVal = b.pod_code || b.pdr_code || '';
        } else if (key === 'mese') {
          aVal = a.pod_data_fine || a.pdr_data_fine;
          bVal = b.pod_data_fine || b.pdr_data_fine;
        } else if (key === 'saldo_totale') {
          aVal = a.cod_pod ? a.saldo_totale_pod : (a.cod_pdr ? a.saldo_totale_pdr : null);
          bVal = b.cod_pod ? b.saldo_totale_pod : (b.cod_pdr ? b.saldo_totale_pdr : null);
        }

        if (dateKeys.includes(key)) {
          const aDate = aVal ? new Date(aVal) : null;
          const bDate = bVal ? new Date(bVal) : null;

          const aTime = aDate instanceof Date && !isNaN(aDate) ? aDate.getTime() : 0;
          const bTime = bDate instanceof Date && !isNaN(bDate) ? bDate.getTime() : 0;

          if (aTime === bTime) return 0;
          return aTime > bTime ? direction : -direction;
        }

        if (numericKeys.includes(key)) {
          const aNum = Number(aVal) || 0;
          const bNum = Number(bVal) || 0;
          if (aNum === bNum) return 0;
          return aNum > bNum ? direction : -direction;
        }

        const aStr = (aVal ?? '').toString();
        const bStr = (bVal ?? '').toString();
        const cmp = aStr.localeCompare(bStr, 'it', { numeric: true, sensitivity: 'base' });
        return cmp * direction;
      });

      return items;
    });

    const handleSort = (key) => {
      if (['cliente', 'rappresentante', 'agente'].includes(key)) return;

      if (sortKey.value === key) {
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
      } else {
        sortKey.value = key;
        sortDirection.value = 'asc';
      }
    };

    const getSortClass = (key) => {
      if (['cliente', 'rappresentante', 'agente'].includes(key)) {
        return '';
      }
      return {
        sortable: true,
        'sorted-asc': sortKey.value === key && sortDirection.value === 'asc',
        'sorted-desc': sortKey.value === key && sortDirection.value === 'desc'
      };
    };

    onMounted(() => {
      loadDepositi(1);
    });

    watch(
      () => pagination.value.current_page,
      (p) => {
        pageInput.value = String(p ?? 1);
      },
      { immediate: true }
    );

    return {
      loading,
      error,
      depositi,
      pagination,
      goToPage,
      pageInput,
      submitPageInput,
      showInsertDialog,
      selectedRow,
      insertLoading,
      insertError,
      insertDialogError,
      openInsertDialog,
      closeInsertDialog,
      confirmInsert,
      formatCurrency,
      formatDate,
      formatMese,
      sortedDepositi,
      handleSort,
      getSortClass,
      sortKey,
      sortDirection
    };
  }
};
</script>

<style scoped>
.gestione-depositi {
  padding: 2rem;
}

.page-header {
  margin-bottom: 1.5rem;
}

.page-header h1 {
  font-size: 2rem;
  margin: 0 0 0.5rem;
  color: #1a202c;
}

.subtitle {
  margin: 0;
  color: #64748b;
}

.page-content {
  margin-top: 1rem;
}

.loader-card,
.error-card,
.empty-state {
  max-width: 900px;
  margin: 0 auto;
  background: #ffffff;
  border-radius: 12px;
  padding: 1.5rem 2rem;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
  text-align: center;
}

.inline-error {
  max-width: 900px;
  margin: 0 auto 0.75rem;
  background: #fff1f2;
  border: 1px solid #fecdd3;
  color: #9f1239;
  border-radius: 10px;
  padding: 0.75rem 1rem;
  text-align: center;
}

.error-card h2 {
  margin-bottom: 0.5rem;
  color: #b91c1c;
}

.table-wrapper {
  max-width: 100%;
  overflow-x: auto;
}

.depositi-table {
  width: 100%;
  border-collapse: collapse;
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

.depositi-table thead {
  background: #0d3c61;
  color: #ffffff;
}

.depositi-table th,
.depositi-table td {
  padding: 0.6rem 0.75rem;
  font-size: 0.85rem;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
}

.th-label {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.sortable {
  cursor: pointer;
  user-select: none;
}

.sorted-asc,
.sorted-desc {
  background: #134a73;
}

.sort-indicator {
  font-size: 0.65rem;
}

.depositi-table tbody tr:nth-child(even) {
  background-color: #f9fafb;
}

.depositi-table tbody tr:hover {
  background-color: #eff6ff;
}

.depositi-table tbody tr.row-deposito-ok {
  background-color: #daf5dd;
}

.depositi-table tbody tr.row-deposito-ok:hover {
  background-color: #c3f5d4;
}

.text-right {
  text-align: right;
}

.text-center {
  text-align: center;
}

.check-ok {
  color: #16a34a;
  font-weight: 700;
}

.cell-link {
  color: #0d3c61;
  text-decoration: none;
  font-weight: 500;
}
.cell-link:hover {
  text-decoration: underline;
}

.pagination-bar {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 0.75rem;
  padding: 0.5rem 0.25rem;
  font-size: 0.85rem;
}

.pagination-info {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 0.2rem;
  padding: 0.5rem 0.25rem;
  font-size: 1rem;
}
.pagination-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  align-items: center;
}

.page-jump {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 0.25rem;
}

.page-jump-label {
  color: #374151;
}

.page-input {
  width: 90px;
  padding: 0.4rem 0.5rem;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  font-size: 0.85rem;
}

.btn {
  padding: 0.4rem 0.8rem;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #374151;
  cursor: pointer;
  font-size: 0.85rem;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.btn-small {
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
}

.btn-primary {
  background: #0d3c61;
  border-color: #0d3c61;
  color: #ffffff;
}

.btn-primary:hover:not(:disabled) {
  background: #134a73;
  color: #ffffff;
}

.btn:hover:not(:disabled) {
  background: #0d3c61;
  color: #ffffff;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 9999;
}

.modal {
  width: 100%;
  max-width: 560px;
  background: #ffffff;
  border-radius: 14px;
  padding: 1.25rem 1.25rem 1rem;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.25);
}

.modal-title {
  margin: 0 0 0.25rem;
  color: #0f172a;
}

.modal-subtitle {
  margin: 0 0 1rem;
  color: #64748b;
  font-size: 0.9rem;
}

.modal-summary {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 0.75rem 0.9rem;
  background: #f8fafc;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.25rem 0;
  font-size: 0.9rem;
}

.summary-label {
  color: #475569;
}

.summary-value {
  color: #0f172a;
  font-weight: 600;
  text-align: right;
}

.modal-error {
  margin-top: 0.75rem;
  background: #fff1f2;
  border: 1px solid #fecdd3;
  color: #9f1239;
  border-radius: 10px;
  padding: 0.6rem 0.75rem;
  font-size: 0.9rem;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
}
</style>
